import type { ColumnDef, Row, Table } from '@tanstack/react-table'
import type { MutableRefObject } from 'react'
import { Checkbox } from 'app/components/ui/checkbox'

export const SELECTION_COLUMN_ID = `__bulkSelect`

/**
 * Toggles a row's selection, with shift-click range support: when shift is held
 * and an anchor exists, selects every row between the anchor and this row (in the
 * current display order). Otherwise toggles the single row and moves the anchor.
 */
export function toggleWithRange<T>(
  table: Table<T>,
  row: Row<T>,
  value: boolean,
  anchorRef: MutableRefObject<string | null>,
  shiftRef: MutableRefObject<boolean>,
) {
  const rows = table.getRowModel().rows

  if (shiftRef.current && anchorRef.current && anchorRef.current !== row.id) {
    const a = rows.findIndex((r) => r.id === anchorRef.current)
    const b = rows.findIndex((r) => r.id === row.id)
    if (a !== -1 && b !== -1) {
      const [lo, hi] = a < b ? [a, b] : [b, a]
      const next = { ...table.getState().rowSelection }
      for (let i = lo; i <= hi; i++) next[rows[i].id] = true
      table.setRowSelection(next)
      anchorRef.current = row.id
      return
    }
  }

  row.toggleSelected(value)
  anchorRef.current = value ? row.id : null
}

/**
 * A leading checkbox column used for bulk selection. Kept out of the column
 * selector via meta.hideInColumnSelector and only made visible while the table
 * is in selection mode (controlled by useTableLayout).
 */
export function selectionColumn<T>(
  anchorRef: MutableRefObject<string | null>,
  shiftRef: MutableRefObject<boolean>,
): ColumnDef<T, any> {
  return {
    id: SELECTION_COLUMN_ID,
    size: 44,
    minSize: 44,
    enableResizing: false,
    enableSorting: false,
    meta: { hideInColumnSelector: true, centered: true },
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows on this page"
        checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? `indeterminate` : false}
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
      />
    ),
    cell: ({ row, table }) => (
      <Checkbox
        aria-label="Select row"
        checked={row.getIsSelected()}
        // Capture the shift key before the (controlled) change fires.
        onClick={(e) => {
          e.stopPropagation()
          shiftRef.current = e.shiftKey
        }}
        onCheckedChange={(value) => toggleWithRange(table, row, !!value, anchorRef, shiftRef)}
      />
    ),
  }
}
